import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Compass, X } from "lucide-react";
import { useReducedMotion } from "../motion";
import { buildTour, CHAPTER_LABELS, SHARED_STEPS, type TourChapterId, type TourStep } from "./tourChapters";

// The guided tour (release 1.5.0, extended for the pilot kick-off on 2026-10-23, D-O115; practices adopted from Summit
// Engineering's "Take the tour"). One shared part over the header and the shell (start page, search, Go to, the data-age
// chip, notifications, the ticker, the menu, VIPER, pilot feedback, the phone and the account menu), then a role chapter
// picked from the person's roles (tourChapters.ts). The tour opens the pages it talks about and waits for them (lazy
// routes); a step whose target never appears is skipped. Progress is remembered per person in this browser, so "Resume
// the tour (step 3 of 17)" picks up where they left. Not modal: the page stays usable; Esc or "Close" stops and keeps the
// place. Follows the person's reduce-motion choice.

export type { TourStep } from "./tourChapters";

/** The shared part, in order (each role's chapter follows it). */
export const TOUR_STEPS: readonly TourStep[] = SHARED_STEPS;

export interface TourProgress {
  /** The step to resume at, or null when not started or finished. */
  step: number | null;
  done: boolean;
}

export const TOUR_PREFIX = "summitops.tour";

/** Raised (by Go to) to start the tour for the signed-in person. */
export const TOUR_START_EVENT = "summitops:tour-start";

/** Version 2 (the role chapters): a person who finished the 1.5.0 tour is offered the new one once. */
export function tourKey(person: string | null | undefined): string {
  return `${TOUR_PREFIX}.v2.${(person ?? "anonymous").toLowerCase()}`;
}

export function readTourProgress(person: string | null | undefined): TourProgress {
  try {
    const raw = window.localStorage.getItem(tourKey(person));
    const p = raw ? (JSON.parse(raw) as Partial<TourProgress>) : null;
    const step = typeof p?.step === "number" && p.step >= 0 && p.step < 100 ? p.step : null;
    return { step, done: p?.done === true };
  } catch {
    return { step: null, done: false };
  }
}

export function writeTourProgress(person: string | null | undefined, progress: TourProgress): void {
  try {
    window.localStorage.setItem(tourKey(person), JSON.stringify(progress));
  } catch {
    // Storage can be unavailable; the tour still runs.
  }
}

/** The menu label: "Take the tour", or "Resume the tour (step 3 of 17)". */
export function tourLabel(progress: TourProgress, total: number = TOUR_STEPS.length): string {
  return progress.step !== null && progress.step < total ? `Resume the tour (step ${progress.step + 1} of ${total})` : "Take the tour";
}

// ── The running tour (one per tab) ───────────────────────────────────────────────────────────────────────────────────

interface TourContext {
  roles: readonly string[];
  assignedRoles?: readonly string[];
  navigate: (to: string) => void;
  pathname: () => string;
}

interface RunningTour {
  person: string | null;
  /** null = the shared part plus the lead chapter (progress is remembered); a chapter = that chapter alone. */
  chapter: TourChapterId | null;
  steps: TourStep[];
  others: TourChapterId[];
  step: number;
  /** False while the tour opens the page and waits for the step's target. */
  ready: boolean;
}

let running: RunningTour | null = null;
let context: TourContext | null = null;
let generation = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Registered by the mounted <HeaderTour />: the person's roles and how to open a page. */
function setTourContext(next: TourContext | null) {
  context = next;
}

function isOnScreen(el: HTMLElement): boolean {
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

function visibleTarget(step: TourStep): HTMLElement | null {
  for (const sel of step.targets) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(sel))) {
      if (isOnScreen(el)) return el;
    }
  }
  return null;
}

/** The next step at or after `from` (in `direction`) whose target is on screen; -1 when there is none. */
export function nextVisibleStep(from: number, direction: 1 | -1, isVisible: (step: TourStep) => boolean, steps: readonly TourStep[] = TOUR_STEPS): number {
  for (let i = from; i >= 0 && i < steps.length; i += direction) if (isVisible(steps[i]!)) return i;
  return -1;
}

const POLL_MS = 120;
/** How long a step on another page may take to appear (lazy route + data). */
export const PAGE_WAIT_MS = 8_000;
/** How long a header step may take (it is normally there at once). */
export const HEADER_WAIT_MS = 1_200;

function normalizePath(p: string): string {
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

/** Opens the step's page when it is elsewhere, then waits for its target. False: skip the step. */
async function reach(step: TourStep, alive: () => boolean): Promise<boolean> {
  const ctx = context;
  if (!ctx) return visibleTarget(step) !== null;
  const path = normalizePath(ctx.pathname());
  let moved = false;
  if (step.routeMatch?.test(path)) {
    // Already on the detail page.
  } else if (step.follow) {
    const link = document.querySelector<HTMLAnchorElement>(step.follow);
    const href = link?.getAttribute("href");
    if (!href) return false;
    ctx.navigate(href);
    moved = true;
  } else if (step.routeMatch) {
    return false;
  } else if (step.route && path !== step.route) {
    ctx.navigate(step.route);
    moved = true;
  }
  const budget = moved || step.route || step.routeMatch ? PAGE_WAIT_MS : HEADER_WAIT_MS;
  for (let waited = 0; ; waited += POLL_MS) {
    if (!alive()) return false;
    if (visibleTarget(step)) return true;
    if (waited >= budget) return false;
    await sleep(POLL_MS);
  }
}

/** Moves to step `to`, skipping (in `direction`) every step whose target does not appear. */
async function goToStep(to: number, direction: 1 | -1): Promise<void> {
  if (!running) return;
  const mine = ++generation;
  const alive = () => mine === generation && running !== null;
  const origin = running.step;
  running = { ...running, step: Math.max(0, Math.min(to, running.steps.length - 1)), ready: false };
  emit();
  let i = to;
  let dir = direction;
  while (alive()) {
    const tour = running!;
    if (i >= tour.steps.length) {
      finishTour();
      return;
    }
    if (i < 0) {
      // Nothing earlier is on screen: stay where we were.
      i = origin;
      dir = 1;
    }
    const ok = await reach(tour.steps[i]!, alive);
    if (!alive()) return;
    if (ok) {
      running = { ...running!, step: i, ready: true };
      if (running.chapter === null) writeTourProgress(running.person, { step: i, done: false });
      emit();
      return;
    }
    i += dir;
  }
}

export function startTour(person: string | null | undefined, fromBeginning: boolean, chapter: TourChapterId | null = null): void {
  const built = buildTour(context?.roles ?? [], context?.assignedRoles, chapter);
  const p = readTourProgress(person);
  const at = chapter || fromBeginning || p.step === null || p.step >= built.steps.length ? 0 : p.step;
  running = { person: person ?? null, chapter, steps: built.steps, others: built.others, step: at, ready: false };
  emit();
  void goToStep(at, 1);
}

export function stopTour(): void {
  generation++;
  running = null;
  emit();
}

function finishTour(): void {
  if (running && running.chapter === null) writeTourProgress(running.person, { step: null, done: true });
  stopTour();
}

function closeTour(): void {
  if (running && running.chapter === null) writeTourProgress(running.person, { step: running.step, done: false });
  stopTour();
}

function useRunningTour() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => running,
    () => running
  );
}

/** The number of steps the tour has for these roles (the account menu's "step 3 of 17"). */
export function tourLength(roles: readonly string[], assignedRoles?: readonly string[]): number {
  return buildTour(roles, assignedRoles).steps.length;
}

export function HeaderTour({ person, roles, assignedRoles }: { person?: string | null; roles?: readonly string[]; assignedRoles?: readonly string[] } = {}) {
  const tour = useRunningTour();
  const titleId = useId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const roleKey = (roles ?? []).join(",");
  const assignedKey = (assignedRoles ?? []).join(",");

  // The live context the running tour reads (roles, how to open a page, where we are).
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  useEffect(() => {
    setTourContext({ roles: roles ?? [], assignedRoles, navigate, pathname: () => pathRef.current });
    return () => setTourContext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, assignedKey, navigate, pathRef]);

  // Go to → "Take the tour".
  useEffect(() => {
    const onStart = () => startTour(person ?? null, readTourProgress(person ?? null).step === null);
    window.addEventListener(TOUR_START_EVENT, onStart);
    return () => window.removeEventListener(TOUR_START_EVENT, onStart);
  }, [person]);

  const step = tour?.ready ? tour.steps[tour.step] : undefined;

  useLayoutEffect(() => {
    if (!step) {
      setRect(null);
      return;
    }
    const el = visibleTarget(step);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight || r.top < 48) {
      el.scrollIntoView?.({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
    }
    setRect(el.getBoundingClientRect());
  }, [step, reduceMotion]);

  useEffect(() => {
    if (!tour) return;
    const place = () => {
      const current = running?.ready ? running.steps[running.step] : undefined;
      const el = current ? visibleTarget(current) : null;
      setRect(el ? el.getBoundingClientRect() : null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeTour();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [tour]);

  if (!tour) return null;

  const total = tour.steps.length;
  const width = Math.min(340, window.innerWidth - 16);

  if (!step || !rect) {
    // Opening the page the next step talks about.
    return createPortal(
      <div
        role="status"
        className="fixed bottom-4 right-4 z-[91] flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-lg print:hidden"
        data-testid="tour-loading"
      >
        <Compass aria-hidden="true" className="h-4 w-4 text-brand-700" />
        Opening the next part of the tour…
        <button type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close the tour" onClick={closeTour} data-testid="tour-close">
          <X aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>,
      document.body
    );
  }

  const index = tour.step;
  const isLast = index >= total - 1;
  const go = (to: number, dir: 1 | -1) => void goToStep(to, dir);

  // Clip the highlight to the window; a tall target (a table) keeps the card in the lower corner instead.
  const top = Math.max(rect.top, 0);
  const bottom = Math.min(rect.bottom, window.innerHeight);
  const tall = bottom - top > window.innerHeight * 0.55;
  const cardHeight = step.kind === "more" ? 230 : 190;
  const below = bottom + 12 + cardHeight < window.innerHeight;
  const left = tall
    ? Math.max(8, window.innerWidth - width - 16)
    : Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8));
  const cardTop = tall ? Math.max(8, window.innerHeight - cardHeight - 16) : below ? bottom + 12 : Math.max(8, top - 12 - cardHeight);

  // Portalled to <body>: the sticky header (backdrop blur) would otherwise be the frame for "fixed".
  return createPortal(
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed z-[90] rounded-lg ring-4 ring-brand-500/70 ring-offset-2 ring-offset-white print:hidden"
        style={{ left: rect.left - 4, top: top - 4, width: rect.width + 8, height: Math.max(0, bottom - top) + 8 }}
        data-testid="tour-highlight"
      />
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        className="fixed z-[91] rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-2xl print:hidden"
        style={{ left, top: cardTop, width }}
        data-testid="tour-card"
        data-step={step.id}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 id={titleId} className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Compass aria-hidden="true" className="h-4 w-4 text-brand-700" />
            {step.title}
          </h2>
          <button type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close the tour" onClick={closeTour} data-testid="tour-close">
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 leading-snug text-slate-700">{step.body}</p>
        {step.kind === "more" && tour.others.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tour-more-chapters">
            {tour.others.map((c) => (
              <button
                key={c}
                type="button"
                className="btn-secondary !px-2 !py-1 text-xs"
                onClick={() => startTour(tour.person, true, c)}
                data-testid={`tour-chapter-${c}`}
              >
                {CHAPTER_LABELS[c]}
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-slate-500" data-testid="tour-count">
            Step {index + 1} of {total}
          </span>
          <div className="flex gap-2">
            {index > 0 && (
              <button type="button" className="btn-secondary inline-flex items-center gap-1 !px-2 text-xs" onClick={() => go(index - 1, -1)} data-testid="tour-back">
                <ChevronLeft aria-hidden="true" className="h-3.5 w-3.5" />
                Back
              </button>
            )}
            {!isLast ? (
              <button type="button" autoFocus className="btn-primary inline-flex items-center gap-1 !px-2 text-xs" onClick={() => go(index + 1, 1)} data-testid="tour-next">
                Next
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button type="button" autoFocus className="btn-primary !px-2 text-xs" onClick={finishTour} data-testid="tour-finish">
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
